import { PropertyType } from '@/api/enumerations';
import { SectionCompletionDetail } from '@/api/manualCalculation/types';
import { AvmFactorsData, GraphsData } from '@/api/workOrders/types';
import { TabContext } from '@mui/lab';
import { Box, Tabs } from '@mui/material';
import { SyntheticEvent, useMemo } from 'react';

import { SectionStatusBadge } from '../components/StatusIndicators';
import { StyledTab, StyledTabPanel, TabContent } from '../styles';
import { getTabStatus } from '../utils/tabStatus';
import { Aggregated } from './components/aggregated';
import { EvaluationResult } from './components/evaluation';
import { FactorsGraphs } from './components/graphs';
import { Homogenization } from './components/homogenization';
import { Scores } from './components/scores';
import { Variation } from './components/variation';
import { FACTOR_TABS } from './constants';

interface FactorsScientificTreatmentProps {
  avmData: AvmFactorsData;
  propertyType: PropertyType;
  graphs: GraphsData[];
  sectionsStatus: Record<string, SectionCompletionDetail> | undefined;
  activeTab: FacTabKey;
  onTabChange: (tab: FacTabKey) => void;
}

export type FacTabKey = typeof FACTOR_TABS[number]['key'];

export function FactorsScientificTreatment({
  avmData,
  propertyType,
  graphs,
  sectionsStatus,
  activeTab,
  onTabChange,
}: FactorsScientificTreatmentProps): JSX.Element {
  const tabStatus = useMemo(
    () => getTabStatus(activeTab, sectionsStatus),
    [activeTab, sectionsStatus]
  );

  const handleTabChange = (_: SyntheticEvent, newTab: FacTabKey): void => {
    onTabChange(newTab);
  };

  const aggregatedFactors = useMemo(
    () =>
      avmData.aggregated_factors.map((f) => ({
        ...f,
        coeff_utilized: f.coeff_utilized ?? false,
        coeff_validation: f.coeff_validation ?? false,
      })),
    [avmData.aggregated_factors]
  );

  return (
    <Box>
      <TabContext value={activeTab}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {FACTOR_TABS.map(({ key, label }) => (
            <StyledTab
              key={key}
              value={key}
              label={<TabContent>{label}</TabContent>}
            />
          ))}
        </Tabs>
        <StyledTabPanel value={activeTab}>
          <SectionStatusBadge status={tabStatus} />
          {activeTab === 'homogenization' && (
            <>
              <Homogenization
                factorsData={avmData.factors}
                propertyType={propertyType}
                aggregatedFactors={aggregatedFactors}
              />
              <FactorsGraphs graphs={graphs} />
            </>
          )}
          {activeTab === 'variation_and_sanitation' && (
            <>
              <Variation variationAgg={avmData.aggregated_input_data} />
              <Aggregated
                aggregated={avmData.aggregated_homogenization}
                elementsCount={avmData.factors.length}
              />
            </>
          )}
          {activeTab === 'foundation' && (
            <Scores
              scores={avmData.scores}
              aggregatedHomogenization={avmData.aggregated_homogenization}
            />
          )}
          {activeTab === 'result' && (
            <EvaluationResult
              aggHomogenizationData={avmData.aggregated_homogenization}
              propertyType={propertyType}
            />
          )}
        </StyledTabPanel>
      </TabContext>
    </Box>
  );
}
